import React, { useState, useEffect } from 'react';

import { API } from 'aws-amplify';
import { Dropdown, Form, Spinner } from 'react-bootstrap';
import { MdHome } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  setTier,
  setDriver,
  setClient,
  setJob,
  setSubmitError,
  resetData,
  setBranch,
  setLocationState,
  setLocationCity,
} from './newRequestSlice';
import { createDQ, getClientWithChecklist, getStates } from './operations';
import {
  Breadcrumbs,
  Button,
  SubHeader,
  ModalConfirmation,
  FormTextField,
  FormSelectField,
  FormRadioField,
  InfoTextField,
} from '../../../../components';
import { getCitiesFiltered } from '../../../ExpenseEntry/DriverSolutions/Hotel/operations';

import './styles.scoped.css';

function tabsBorderRadius() {
  // eslint-disable-next-line no-undef
  document.querySelectorAll('.rounded-top-left').forEach(elem => {
    elem.style.borderTopLeftRadius = '15px';
  });
  // eslint-disable-next-line no-undef
  document.querySelectorAll('.rounded-top-right').forEach(elem => {
    elem.style.borderTopRightRadius = '15px';
  });
}

const NewRequest = () => {
  const { id, name } = useSelector(state => state.admin.user);
  const { tierData, driver, client, job, tier, branch, locationState, locationCity, submitError } = useSelector(
    state => state.newRequest,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createDqUserName = `${name}`;
  const [showForm, setShowForm] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [dqId, setDqId] = useState();
  const apiName = 'centerlineportal';
  const [locationData, setLocationData] = useState();
  const [searching, setSearching] = useState(false);
  const [loadingCitiesData, setLoadingCitiesData] = useState(false);
  const [citiesError, setCitiesError] = useState(false);
  const [checklistError, setChecklistError] = useState(false);
  const [checkListLevel, setCheckListLevel] = useState(null);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line no-undef
  window.onpopstate = () => {
    clearForm();
  };
  const clearCityData = () => {
    dispatch(
      setLocationCity({
        cityId: '',
        cityName: '',
        isValid: true,
      }),
    );
    setLocationData({
      ...locationData,
      cities: [],
    });
    setLoadingCitiesData(false);
    setCitiesError(false);
  };

  const getAndSetFormData = async () => {
    const states = await getStates();
    if (states) {
      setLocationData({ ...locationData, states: states });
    }
  };

  let currentCities = [];
  let prevToken = '';

  const getCitiesBatch = async (nextToken = null) => {
    setLoadingCitiesData(true);
    if (prevToken !== nextToken) {
      prevToken = nextToken;
      const data = await getCitiesFiltered({
        state: locationState.stateId,
        token: nextToken,
      });
      const token = data?.nextToken;

      if (data?.items) {
        const items = data?.items;
        currentCities = currentCities.concat(items);
        if (data?.items.length === 0) {
          setCitiesError(true);
        }
      }
      if (token) {
        getCitiesBatch(token);
      } else {
        dispatch(
          setLocationCity({
            cityId: '',
            cityName: '',
            isValid: true,
          }),
        );
        setLocationData({ ...locationData, cities: currentCities });
      }
    }
    setLoadingCitiesData(false);
  };

  const handleStateChange = async () => {
    setCitiesError(false);
    setLoadingCitiesData(true);
    await getCitiesBatch();
    setLoadingCitiesData(false);
  };

  useEffect(() => {
    if (locationState.stateId) {
      handleStateChange();
    }
  }, [locationState.stateId]);

  const stateOptions = locationData?.states
    ?.sort((a, b) => (a.name > b.name ? 1 : -1))
    .map(({ name: aName, id: anId }) => ({
      label: aName,
      value: anId,
    }));

  const cityOptions = locationData?.cities
    ?.sort((a, b) => (a.name > b.name ? 1 : -1))
    .map(({ name: aName, id: anId }) => ({
      label: aName,
      value: anId,
    }));

  useEffect(() => {
    getAndSetFormData();
  }, []);

  const homeButton = () => {
    clearForm();
    navigate('../driver-qualification', { replace: true });
  };

  const fetchDriver = async data => {
    if (!data.driverId) {
      dispatch(
        setDriver({
          ...driver,
          isValid: false,
        }),
      );
      return;
    }
    setSearching(true);
    const path = `drivers/${data.driverId}`;
    try {
      const response = await API.get(apiName, path);
      if (response.response_code === 200) {
        const result = response.response_body;
        dispatch(
          setDriver({
            ...driver,
            isValid: true,
            name: `${result.driverFirstName} ${result.driverLastName}`,
            status: result.driverStatus,
            skills: result.driverSkills,
            cityState: `${result.driverCity}, ${result.driverState}`,
            disable: true,
            fetchSuccess: true,
          }),
        );
        setShowForm(true);
      }
    } catch (e) {
      dispatch(
        setDriver({
          ...driver,
          isValid: false,
          disable: false,
          fetchSuccess: false,
        }),
      );
    }
    setSearching(false);
  };

  const detectCheckListLevel = async info => {
    if (info?.clientId) {
      const [singleClient, cldClient, amcClient] = await Promise.all([
        getClientWithChecklist({ clientId: info?.clientId, checkListLevel: 'Single Client' }),
        getClientWithChecklist({ clientId: 0, checkListLevel: 'CLD Default' }),
        getClientWithChecklist({
          clientId: 0,
          checkListLevel: 'AMC',
          accountMasterCode: info?.clientAccountMasterCode,
        }),
      ]);

      if (singleClient || cldClient || amcClient) {
        if (singleClient) {
          setCheckListLevel('Single Client');
          dispatch(setTier({ ...tier, isValid: false }));
        } else if (amcClient) {
          setCheckListLevel('AMC');
          dispatch(setTier({ ...tier, isValid: true, isSelected: '', value: 'Select...' }));
        } else {
          setCheckListLevel('CLD Default');
          dispatch(setTier({ ...tier, isValid: true, isSelected: '', value: 'Select...' }));
        }
        setChecklistError(false);
      } else {
        setCheckListLevel(null);
        setChecklistError(true);
      }
    } else {
      setCheckListLevel(null);
    }
  };

  const fetchClient = async data => {
    setLoading(true);
    const path = `clients/${data.clientId}`;
    let newClient = {
      ...client,
      clientAccountMasterCode: null,
      clientId: '',
      status: '',
      name: '',
      isValid: false,
      fetchSuccess: false,
    };
    try {
      const response = await API.get(apiName, path);
      if (response.response_code === 200) {
        const result = response.response_body;
        newClient = {
          ...client,
          clientAccountMasterCode: result.clientAccountMasterCode,
          clientId: result.clientId,
          status: result.clientStatus,
          name: result.clientName,
          isValid: true,
          fetchSuccess: true,
        };
        dispatch(setClient(newClient));
      }
    } catch (e) {
      dispatch(setClient(newClient));
    }
    await detectCheckListLevel(newClient);
    setLoading(false);
  };

  const fetchJobOrder = async data => {
    if (!data.jobOrderId) return;
    dispatch(setJob({ ...data, fetching: true }));
    const path = `job-orders/${data.jobOrderId}`;
    try {
      const response = await API.get(apiName, path);
      const code = response?.response_code;
      const body = response?.response_body;
      if (code === 200 && body?.jobStartDate !== undefined) {
        dispatch(
          setJob({
            ...data,
            startDate: body.jobStartDate,
            status: body.jobStatus,
            isValid: true,
            fetchSuccess: true,
            fetching: false,
          }),
        );
      } else {
        dispatch(setJob({ ...data, startDate: '', status: '', isValid: false, fetchSuccess: false, fetching: false }));
      }
    } catch (e) {
      dispatch(setJob({ ...data, startDate: '', status: '', isValid: false, fetchSuccess: false, fetching: false }));
    }
  };

  const fetchNewDqId = async () => {
    const path = '/dqf-ids';
    try {
      const response = await API.get(apiName, path, {
        body: {},
      });
      if (response) {
        setDqId(response?.response_body.id);
        return response?.response_body.id;
      }
      return undefined;
    } catch (error) {
      console.log('Fetch new DQ Id error:', error);
      return undefined;
    }
  };

  const assignTier = value => {
    dispatch(setTier({ ...tier, isValid: true, isSelected: value, value: value }));
  };

  const clearForm = () => {
    dispatch(resetData());
    setShowForm(false);
    setPostSuccess(false);
  };

  const tierIsValid = () => {
    let isValid = false;
    if (tier.value !== 'Select...' || checkListLevel !== 'Single Client') {
      isValid = true;
      if (tier.value === '2') {
        if (tier.description) {
          isValid = true;
        } else {
          isValid = false;
        }
      }
    }

    return isValid;
  };

  const isValid = async () => {
    setLoading(true);
    if (
      driver.fetchSuccess &&
      client.fetchSuccess &&
      branch?.branchId &&
      tierIsValid() &&
      locationCity.cityId &&
      locationState.stateId
    ) {
      await postDQ();
    } else {
      if (!client.fetchSuccess) {
        dispatch(setClient({ ...client, isValid: false }));
      }

      if (!locationState.stateId) {
        dispatch(setLocationState({ ...locationState, isValid: false }));
      }

      if (!locationCity.cityId) {
        dispatch(
          setLocationCity({
            ...locationCity,
            isValid: false,
          }),
        );
      }

      if (!branch?.branchId) {
        dispatch(
          setBranch({
            ...branch,
            branchId: '',
            isValid: false,
          }),
        );
      }
    }

    setLoading(false);
  };

  const postDQ = async () => {
    dispatch(setSubmitError(''));
    const adqId = await fetchNewDqId();

    try {
      const response = await createDQ(
        driver,
        client,
        job,
        tier.value === 'Select...' && checkListLevel !== 'Single Client' ? { value: 0 } : tier,
        adqId,
        id,
        createDqUserName,
        branch,
        locationState,
        locationCity,
        checkListLevel,
      );
      if (response?.data) {
        setPostSuccess(true);
      } else {
        dispatch(setSubmitError('There was an error creating the DQF request. Please try again.'));
      }
    } catch (e) {
      dispatch(setSubmitError('There was an error creating the DQF request. Please try again.'));
    }
  };

  useEffect(() => {
    tabsBorderRadius();
  }, []);

  return (
    <>
      <SubHeader
        text="Centerline Driver Qualification"
        buttons={[{ icon: <MdHome size={30} color="#FFF" />, onPress: homeButton }]}
        popoverTitle="Need help with your driver qualification?"
      />
      <Breadcrumbs
        leftTitle="DQF Home"
        midItemAction={() => {
          setShowForm(false);
          dispatch(
            setDriver({
              ...driver,
              driverId: '',
              isValid: true,
              disable: false,
            }),
          );
        }}
        middleTitle={'Select Driver '}
        rightTitle={driver.driverId && 'New Request'}
        route="/driver-qualification"
      />
      <div style={{ height: '100vh', padding: '0 15px' }}>
        <div style={{ maxWidth: '100%', padding: '3rem' }}>
          <p className="text-center rounded-top-left rounded-top-right p-2 text-white bg-primary mx-100 m-0">
            Request DQF
          </p>
          <div className="border border-primary">
            <div className="form-group">
              <div>
                <div className="bold-column">Driver Information</div>

                <div style={{ marginBottom: '24px' }} className="form-section">
                  <FormTextField
                    label="Driver Id"
                    required
                    style={{ gridTemplateColumns: '1fr' }}
                    value={driver.driverId}
                    onChange={e => {
                      dispatch(setDriver({ ...driver, driverId: e.target.value, isValid: true }));
                    }}
                    disabled={driver.disable || searching}
                    errorMessage="Please enter a valid Driver ID"
                    error={!driver.isValid}
                  />
                  {showForm && (
                    <>
                      <InfoTextField label="Driver Name" value={driver.name} />
                      <InfoTextField label="Driver Status" value={driver.status} />
                    </>
                  )}
                </div>

                {!showForm && (
                  <div className="btn-center">
                    <Button variant="primary" className="btn" onPress={() => fetchDriver(driver)}>
                      {searching ? <Spinner animation="border" size="sm" style={{ margin: 24 }} /> : 'Search'}
                    </Button>
                  </div>
                )}

                {showForm && (
                  <>
                    <div className="form-section">
                      <InfoTextField label="City, State" value={driver?.cityState} />
                      <InfoTextField label="Skills">
                        <div className="scroll-list">
                          {driver.skills.map(item => (
                            <div className="gray-text" key={item?.id}>
                              {`${item?.skillName}: ${item?.skillDescription}`}
                            </div>
                          ))}
                        </div>
                      </InfoTextField>
                    </div>

                    <div className="bold-column">Client Information</div>

                    <div style={{ marginBottom: '24px' }} className="form-section">
                      <FormTextField
                        label="Client Id"
                        required
                        style={{ gridTemplateColumns: '1fr' }}
                        value={client.clientId}
                        onChange={e => {
                          dispatch(setClient({ ...client, clientId: e.target.value, isValid: true }));
                        }}
                        onBlur={() => fetchClient(client)}
                        errorMessage="Please enter a valid Client ID"
                        error={!client.isValid}
                      />
                      <InfoTextField label="Client Name" value={client.name} />
                      <InfoTextField label="Client Status" value={client.status} />
                    </div>

                    <div className="form-section">
                      <div style={{ marginBottom: '24px' }}>
                        <FormTextField
                          label="Job Order (optional)"
                          style={{ gridTemplateColumns: '1fr' }}
                          value={job.jobOrderId || null}
                          onChange={e => {
                            dispatch(setJob({ ...job, jobOrderId: e.target.value, isValid: true }));
                          }}
                          onBlur={() => fetchJobOrder(job)}
                          errorMessage="Please enter a valid Job Order ID"
                          error={!job.isValid}
                        />
                        {job.fetching && <Spinner animation="border" size="sm" style={{ marginTop: 4 }} />}
                      </div>
                      <InfoTextField label="Job Start Date" value={job.startDate} />
                      <InfoTextField label="Job Status" value={job.status} />
                    </div>

                    <div style={{ marginBottom: '24px' }} className="form-section">
                      <FormTextField
                        label="Branch"
                        required
                        style={{ gridTemplateColumns: '1fr' }}
                        value={branch?.branchId}
                        onChange={e => {
                          const re = /^[0-9\b]+$/;
                          if (e.target.value === '' || re.test(e.target.value)) {
                            dispatch(setBranch({ ...branch, branchId: e.target.value, isValid: true }));
                          }
                        }}
                        errorMessage="Please enter a valid Branch ID"
                        error={!branch?.isValid}
                      />
                    </div>

                    <div className="form-section">
                      <div style={{ marginBottom: '24px' }}>
                        <FormSelectField
                          label="State"
                          required
                          isClearable
                          style={{ gridTemplateColumns: '1fr', width: '350px' }}
                          options={stateOptions}
                          value={stateOptions?.filter(state => state.value === locationState.stateId)}
                          onChange={(event, action) => {
                            if (action.action === 'clear') {
                              dispatch(setLocationState({ stateId: '', stateName: '', isValid: false }));
                              clearCityData();
                            }
                            if (action.action === 'select-option') {
                              dispatch(setLocationState({ stateId: event.value, stateName: event.label, isValid: true }));
                              clearCityData();
                            }
                          }}
                          error={!locationState.isValid}
                          errorMessage="Please select a State"
                        />
                      </div>
                      <div>
                        <FormSelectField
                          label="City"
                          required
                          isClearable
                          style={{ gridTemplateColumns: '1fr' }}
                          isDisabled={!locationState?.stateId || !locationData?.cities?.length}
                          loading={loadingCitiesData && !!locationState.stateId}
                          options={cityOptions}
                          value={cityOptions?.filter(city => city.value === locationCity.cityId)}
                          onChange={(event, action) => {
                            if (action.action === 'clear') {
                              dispatch(setLocationCity({ cityId: '', cityName: '', isValid: false }));
                            }
                            if (action.action === 'select-option') {
                              dispatch(setLocationCity({ cityId: event.value, cityName: event.label, isValid: true }));
                            }
                          }}
                          error={(!locationCity.isValid && !!locationState.stateId) || citiesError}
                          errorMessage={citiesError ? 'No Cities available' : (!!locationState.stateId && 'Please select a City')}
                        />
                      </div>
                    </div>

                    <div className="form-section" style={{ minHeight: 80 }}>
                      <FormRadioField
                        label="Checklist Level"
                        style={{ gridTemplateColumns: '1fr' }}
                        options={[
                          { value: 'CLD Default', label: 'CLD Default' },
                          { value: 'AMC', label: 'AMC' },
                          { value: 'Single Client', label: 'Single Client' },
                        ]}
                        value={checkListLevel || ''}
                        onChange={() => { }}
                        disabled
                        name="checklistLevel"
                      />
                      {checkListLevel === 'AMC' && (
                        <InfoTextField label="AMC Code" value={client?.clientAccountMasterCode} />
                      )}
                    </div>

                    <div className="bold-column">Driver Qualification Tier</div>

                    <div className="form-section">
                      <div>
                        <Form.Label>
                          Tier <span>*</span>
                        </Form.Label>
                        <Dropdown
                          style={{ width: '100%' }}
                          onSelect={eventKey => {
                            assignTier(eventKey);
                          }}>
                          <Dropdown.Toggle
                            style={{ width: '85%' }}
                            className="p-2"
                            disabled={checkListLevel !== 'Single Client'}>
                            {tier.value !== 'Select...'
                              ? tierData.find(tierD => String(tierD.value) === String(tier.value))?.label
                              : 'Select...'}
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ width: '100%' }}>
                            {tierData.map(item => (
                              <Dropdown.Item eventKey={item.value} key={item.value}>
                                {item.label}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        {!tier.isValid && (
                          <div className="invalid-input">Please select a Tier</div>
                        )}
                      </div>
                      {tier.value === '2' && (
                        <FormTextField
                          label="Other Description"
                          style={{ gridTemplateColumns: '1fr' }}
                          value={tier.description}
                          onChange={e => {
                            dispatch(setTier({ ...tier, description: e.target.value }));
                          }}
                          errorMessage="Please enter a valid description"
                          error={!tier.description}
                        />
                      )}
                    </div>
                  </>
                )}
                {postSuccess && (
                  <ModalConfirmation
                    title="Success!"
                    body={`Driver Qualification ${dqId} requested successfully.`}
                    primaryButtonTitle="Close"
                    onClick={() => {
                      clearForm();
                      navigate('/driver-qualification', { replace: true });
                    }}
                    isOpen={postSuccess}
                    primaryButton
                  />
                )}
              </div>
            </div>
            {showForm && (
              <div className="btn-wrapper">
                {submitError && <p className="error">{submitError}</p>}
                <Button
                  variant="primary"
                  className="btn"
                  onPress={() => {
                    isValid();
                  }}
                  disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" style={{ margin: 24 }} /> : 'Request'}
                </Button>
              </div>
            )}
          </div>
          <ModalConfirmation
            isOpen={checklistError}
            title="Checklist Error"
            onClick={() => {
              setChecklistError(false);
              setCheckListLevel(null);
              dispatch(
                setClient({
                  ...(client || {}),
                  clientAccountMasterCode: null,
                  clientId: '',
                  status: '',
                  name: '',
                  isValid: false,
                  fetchSuccess: false,
                }),
              );
            }}
            primaryButton
            primaryButtonTitle="Ok"
            body="The user selected has no checklist defined. A checklist must be defined to create a new DQF request."
          />
        </div>
      </div>
    </>
  );
};

export default NewRequest;
